import { BlockBaseCommand } from './BlockBaseCommand';
import type { BlockCommandDependencies } from './BlockBaseCommand';
import type { SignatureBox } from 'services/entities/SignatureTypes';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { addGridBlockState, deleteGridBlockState } from '../grid/reduxStore/editorSlice';
import { GridBlockType } from 'services/entities/BlockTypes';
import { addSignatureViaSocket, deleteSignatureViaSocket } from '../grid/reduxStore/saveHandlers';

export interface SignatureInsertCommandParams {
  sectionId: string;
  signature: SignatureBox;
}
const SIGNATURE_Z_INDEX = 1;

export class SignatureInsertCommand extends BlockBaseCommand {
  readonly sectionId: string;
  private signatureId: string;
  private readonly signature: SignatureBox;

  constructor(dependencies: BlockCommandDependencies, params: SignatureInsertCommandParams) {
    super(dependencies);
    this.sectionId = params.sectionId;
    this.signature = structuredClone(params.signature);
    this.signatureId = params.signature.signatureBoxId;
  }

  getCurrentBlockId(): string {
    return this.signatureId;
  }

  // The backend generates the persisted id server-side and ignores the client's signatureBoxId,
  // so nothing is dispatched into Redux until the ack confirms the real id — matching
  // CreateBlockCommand's pattern, this never renders under a client-generated id that would
  // later need to be swapped out.
  private applyGridBlockState(): void {
    const blockConfig = {
      id: this.signatureId,
      x: this.signature.properties.position.x,
      y: this.signature.properties.position.y,
      width: this.signature.properties.dimensions.width,
      height: this.signature.properties.dimensions.height,
      z: SIGNATURE_Z_INDEX,
    };

    this.dispatch(
      addGridBlockState({
        sectionId: this.sectionId,
        blockId: this.signatureId,
        content: this.signature,
        blockConfig,
        blockType: GridBlockType.SIGNATURE,
      })
    );
  }

  private async insert(): Promise<boolean> {
    try {
      this.setSaving();

      const response = await addSignatureViaSocket(this.socketClient, this.sectionId, this.signature);

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.setNotSaved();
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.setNotSaved();
        return false;
      }

      this.signatureId = response.content.id;
      this.signature.signatureBoxId = this.signatureId;
      this.applyGridBlockState();

      this.recalculatePageHeight(this.sectionId);

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          ...(response.errorCode ? { errorCode: response.errorCode } : null),
        })
      );
      return true;
    } catch (error) {
      console.error('SignatureInsertCommand: insert failed', error);
      this.setNotSaved();
      return false;
    }
  }

  async persistToSocket(): Promise<boolean> {
    return this.insert();
  }

  async undo(): Promise<boolean> {
    try {
      this.setSaving();

      this.dispatch(deleteGridBlockState({ sectionId: this.sectionId, blockId: this.signatureId }));

      this.recalculatePageHeight(this.sectionId);

      const response = await deleteSignatureViaSocket(this.socketClient, this.signature);

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.rollbackUndo();
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.rollbackUndo();
        return false;
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          ...(response.errorCode ? { errorCode: response.errorCode } : null),
        })
      );
      return true;
    } catch (error) {
      console.error('SignatureInsertCommand: undo failed', error);
      this.rollbackUndo();
      return false;
    }
  }

  async redo(): Promise<boolean> {
    return this.insert();
  }

  private rollbackUndo(): void {
    this.applyGridBlockState();
    this.setNotSaved();
  }

  toJSON(): Record<string, any> {
    return {
      signatureId: this.signatureId,
      sectionId: this.sectionId,
      signature: this.signature,
    };
  }
}
